import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import it from './locales/it.json';
import en from './locales/en.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import es from './locales/es.json';
import nl from './locales/nl.json';
import pl from './locales/pl.json';
import pt from './locales/pt.json';

// Mapping nazionalità/paesi a codici lingua
const NATIONALITY_TO_LANGUAGE = {
  // Italiano
  'italia': 'it', 'italiana': 'it', 'italian': 'it', 'italy': 'it',
  // Tedesco
  'germania': 'de', 'tedesca': 'de', 'german': 'de', 'germany': 'de',
  'austria': 'de', 'austriaca': 'de', 'austrian': 'de',
  'svizzera': 'de', 'switzerland': 'de', 'swiss': 'de',
  // Francese
  'francia': 'fr', 'francese': 'fr', 'french': 'fr', 'france': 'fr',
  'belgio': 'fr', 'belgium': 'fr', 'belgian': 'fr',
  'lussemburgo': 'fr', 'luxembourg': 'fr',
  // Spagnolo
  'spagna': 'es', 'spagnola': 'es', 'spanish': 'es', 'spain': 'es',
  'messico': 'es', 'mexico': 'es', 'mexican': 'es',
  'argentina': 'es', 'argentina': 'es',
  'cile': 'es', 'chile': 'es',
  'colombia': 'es', 'colombian': 'es',
  'peru': 'es', 'peruvian': 'es',
  // Olandese
  'paesi bassi': 'nl', 'olanda': 'nl', 'netherlands': 'nl', 'dutch': 'nl',
  // Polacco
  'polonia': 'pl', 'polacca': 'pl', 'polish': 'pl', 'poland': 'pl',
  // Portoghese
  'portogallo': 'pt', 'portoghese': 'pt', 'portuguese': 'pt', 'portugal': 'pt',
  'brasile': 'pt', 'brazil': 'pt', 'brazilian': 'pt',
  // Inglese - default per tutti gli altri
  'regno unito': 'en', 'uk': 'en', 'british': 'en', 'united kingdom': 'en',
  'stati uniti': 'en', 'usa': 'en', 'american': 'en', 'united states': 'en',
  'irlanda': 'en', 'ireland': 'en', 'irish': 'en',
  'australia': 'en', 'australian': 'en',
  'canada': 'en', 'canadian': 'en',
};

// Mapping prefissi telefonici a lingua
const PHONE_PREFIX_TO_LANGUAGE = {
  '+39': 'it', '0039': 'it',    // Italia
  '+49': 'de', '0049': 'de',    // Germania
  '+43': 'de', '0043': 'de',    // Austria
  '+41': 'de', '0041': 'de',    // Svizzera
  '+33': 'fr', '0033': 'fr',    // Francia
  '+32': 'fr', '0032': 'fr',    // Belgio
  '+352': 'fr',                  // Lussemburgo
  '+34': 'es', '0034': 'es',    // Spagna
  '+52': 'es',                   // Messico
  '+54': 'es',                   // Argentina
  '+56': 'es',                   // Cile
  '+57': 'es',                   // Colombia
  '+51': 'es',                   // Peru
  '+31': 'nl', '0031': 'nl',    // Paesi Bassi
  '+48': 'pl', '0048': 'pl',    // Polonia
  '+351': 'pt', '00351': 'pt',  // Portogallo
  '+55': 'pt', '0055': 'pt',    // Brasile
  '+44': 'en', '0044': 'en',    // UK
  '+1': 'en',                    // USA/Canada
  '+353': 'en',                  // Irlanda
  '+61': 'en',                   // Australia
};

// Funzione per rilevare lingua da nazionalità
export const detectLanguageFromNationality = (nationality) => {
  if (!nationality) return 'en';
  const normalized = nationality.toLowerCase().trim();
  return NATIONALITY_TO_LANGUAGE[normalized] || 'en';
};

// Funzione per rilevare lingua da telefono
export const detectLanguageFromPhone = (phone) => {
  if (!phone) return null;
  const cleaned = phone.replace(/\s/g, '');
  
  for (const [prefix, lang] of Object.entries(PHONE_PREFIX_TO_LANGUAGE)) {
    if (cleaned.startsWith(prefix)) {
      return lang;
    }
  }
  return null;
};

// Lingue supportate
export const SUPPORTED_LANGUAGES = [
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      it: { translation: it },
      en: { translation: en },
      de: { translation: de },
      fr: { translation: fr },
      es: { translation: es },
      nl: { translation: nl },
      pl: { translation: pl },
      pt: { translation: pt },
    },
    fallbackLng: 'en',
    supportedLngs: ['it', 'en', 'de', 'fr', 'es', 'nl', 'pl', 'pt'],
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
    },
  });

export default i18n;
